/* eslint-disable spire/fenced-imports */
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import translate from "@insite/client-framework/Translate";
import { LinkFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button from "@insite/mobius/Button";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import TextField from "@insite/mobius/TextField";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import { EditorTemplateProps } from "@insite/shell-public/EditorTemplateProps";
import { isValidUrl } from "@insite/shell/Common/IsValidUrl";
import { ClickOutsideWrapper } from "@insite/shell/Components/ClickOutside";
import AxiomIcon from "@insite/shell/Components/Icons/AxiomIcon";
import NeverPublishedModal from "@insite/shell/Components/Modals/NeverPublishedModal";
import shellTheme, { ShellThemeProps } from "@insite/shell/ShellTheme";
import ShellState from "@insite/shell/Store/ShellState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

interface State {
    linkSelectorIsOpen: boolean;
    expandedKeys: string[];
    url: string;
    isValidUrl: boolean;
    triedToSaveUrl: boolean;
    currentTab?: string;
}

type OwnProps = EditorTemplateProps<LinkFieldValue, LinkFieldDefinition>;

const mapStateToProps = (state: ShellState, ownProps: OwnProps) => {
    ownProps.fieldValue =
        ownProps.fieldValue !== undefined && ownProps.fieldValue !== null
            ? ownProps.fieldValue
            : ownProps.fieldDefinition.defaultValue;
    const fieldValue = ownProps.fieldValue;
    let displayValue = "";
    if (fieldValue) {
        displayValue = fieldValue.value;
    }
    return {
        displayValue,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StandardIconPropsSource: React.FC = () => <AxiomIcon src="link" color={shellTheme.colors.text.main} />;
const ClearIconPropsSource: React.FC = () => <AxiomIcon color="#000" src="times" />;

class CustomExternalLinkField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            linkSelectorIsOpen: false,
            expandedKeys: [emptyGuid],
            url: props.fieldValue.type === "Url" ? props.fieldValue.value : "",
            isValidUrl: true,
            triedToSaveUrl: false,
            currentTab: "Url",
        };
    }

    onClickOutside = () => {
        this.setState({
            linkSelectorIsOpen: false,
            currentTab: "Url",
        });
    };

    clickLinkField = (event: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            linkSelectorIsOpen: true,
        });
    };

    changeExternalUrl = (event: React.FormEvent<HTMLInputElement>) => {
        let isValidExternalUrl = true;
        if (this.state.triedToSaveUrl && !this.state.isValidUrl && !isValidUrl(event.currentTarget.value)) {
            isValidExternalUrl = false;
        }

        this.setState({
            url: event.currentTarget.value,
            isValidUrl: isValidExternalUrl,
        });
    };

    clickSaveExternalUrl = () => {
        if (!isValidUrl(this.state.url)) {
            this.setState({
                isValidUrl: false,
                triedToSaveUrl: true,
            });
            return;
        }

        this.props.updateField(this.props.fieldDefinition.name, {
            type: "Url",
            value: this.state.url,
        });
        this.setState({
            linkSelectorIsOpen: false,
            isValidUrl: true,
            triedToSaveUrl: false,
        });
    };

    externalUrlKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            this.clickSaveExternalUrl();
        }
    };

    clearFieldValue = () => {
        this.props.updateField(this.props.fieldDefinition.name, {
            ...this.props.fieldValue,
            value: "",
        });
    };

    render() {
        const {
            fieldValue: { value },
            fieldDefinition,
        } = this.props;
        const tabs = [
            <Tab data-test-selector="tab_Url" headline="Url" key="Url" tabKey="Url" css={tabCss}>
                <ExternalStyle>
                    <TextField
                        label="Url"
                        value={this.state.url}
                        error={this.state.isValidUrl ? "" : "Please enter a valid URL"}
                        onChange={this.changeExternalUrl}
                        onKeyPress={this.externalUrlKeyPress}
                        data-test-selector="linkField_Url"
                    />
                    <Button
                        variant="secondary"
                        onClick={this.clickSaveExternalUrl}
                        data-test-selector="linkField_Url_Save"
                    >
                        Save
                    </Button>
                </ExternalStyle>
            </Tab>,
        ];

        return (
            <StandardControl fieldDefinition={fieldDefinition}>
                <TextField
                    readOnly={true}
                    onClick={this.clickLinkField}
                    id={fieldDefinition.name}
                    type="text"
                    value={this.props.displayValue}
                    iconProps={{ src: value ? ClearIconPropsSource : StandardIconPropsSource }}
                    iconClickableProps={value ? { onClick: this.clearFieldValue } : undefined}
                    clickableText={value ? translate("clear link field") : undefined}
                />
                <ClickOutsideWrapper handleClick={this.onClickOutside}>
                    {this.state.linkSelectorIsOpen && (
                        <LinkSelectorStyle>
                            <TabGroup
                                onTabChange={(_, currentTab) => this.setState({ currentTab })}
                                cssOverrides={{ tabContent, tabGroup, wrapper }}
                                current={this.state.currentTab}
                            >
                                {tabs}
                            </TabGroup>
                        </LinkSelectorStyle>
                    )}
                </ClickOutsideWrapper>
                <NeverPublishedModal updateField={this.props.updateField} />
            </StandardControl>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomExternalLinkField);

const tabCss: FlattenSimpleInterpolation = css`
    padding: 6px;
    &:hover {
        cursor: ${(props: TabProps) => (props.selected ? "default" : "pointer")};
    }
    font-weight: ${(props: TabProps) => (props.selected ? "bold" : "inherit")};
` as FlattenSimpleInterpolation;

const tabContent = css`
    border-bottom: none;
    padding: 16px;
    max-height: 463px;
    overflow: auto;
`;

const tabGroup = css`
    padding: 0;
`;

const wrapper = css`
    padding: 0;
`;

const LinkSelectorStyle = styled.div`
    margin-top: 2px;
    position: absolute;
    background-color: ${(props: ShellThemeProps) => props.theme.colors.common.accent};
    width: 288px;
    z-index: 5010;
    border: 1px solid ${(props: ShellThemeProps) => props.theme.colors.common.border};
    border-radius: 3px;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
    height: 500px;
`;

const ExternalStyle = styled.div`
    button {
        margin-top: 16px;
        float: right;
    }
    label {
        font-size: 15px;
        margin-bottom: -4px;
    }
`;
